import {
  GetCeoHealthCompaniesQuery,
  GetCeoHealthDealsQuery,
  GetCeoHealthTimeEntriesQuery,
  GetCeoHealthUsersQuery,
} from "@/src/gql/graphql";
import { HealthMetrics } from "@/lib/types/database/deals";
import { validateDealRevenue, validateDealCost } from "@/lib/utils/crm/deal-data-validation";
import { computeBurdenedLaborCost } from "@/lib/finance/labor-cost";
// The NARROW subpath, not the `@wtc/ui` root barrel. This module is now in the
// server graph of `app/api/workspace-addon/owner-glance`, and a root-barrel
// import there drags everything the package exports (react-hook-form included)
// into a route build — `SERVER_BARREL_IMPORTS`, and the shape of the 2026-08-22
// prod build failure.
import { parseBigFloat } from "@west-tennessee-consulting/ui/lib/currency";
import {
  classifyCompanyHealth,
  type CompanyHealthStatusInput,
} from "@/components/admin/ceo-dashboard/health-dimensions";

type CompaniesCollection = NonNullable<GetCeoHealthCompaniesQuery["companiesCollection"]>;
type DealsCollection = NonNullable<GetCeoHealthDealsQuery["dealsCollection"]>;
type TimeEntriesCollection = NonNullable<GetCeoHealthTimeEntriesQuery["time_entriesCollection"]>;
type UsersCollection = NonNullable<GetCeoHealthUsersQuery["app_usersCollection"]>;

export type HealthCompanyNode = CompaniesCollection["edges"][number]["node"];
export type HealthDealNode = DealsCollection["edges"][number]["node"];
export type HealthTimeEntryNode = TimeEntriesCollection["edges"][number]["node"];
export type HealthUserNode = UsersCollection["edges"][number]["node"];

// ---------------------------------------------------------------------------
// What this calculator actually READS
// ---------------------------------------------------------------------------
//
// The four `*Node` aliases above describe the pg_graphql TRANSPORT the browser
// hook happens to use. `calculateHealthMetrics` itself is pure arithmetic and
// touches only the handful of fields below, so it takes these structural
// shapes instead — the GraphQL nodes satisfy them, and so do PostgREST rows.
//
// This matters because the same four numbers are now served to `apps/ios` by
// `GET /api/workspace-addon/owner-glance`, which reads over PostgREST (there is
// no Apollo client on a route handler). Typing the parameters on the GraphQL
// nodes would have forced that route to launder its rows through
// `as unknown as HealthCompanyNode[]` — a cast that compiles no matter how
// wrong the fields are, and therefore exactly the assertion `main-03` forbids.
// Widening the parameters instead lets `tsc` check both callers for real.
//
// Every money/hours field accepts `string` as well as `number`: these are
// Postgres `numeric`, which pg_graphql serializes as a BigFloat STRING, and
// `parseBigFloat` is what normalises them.

/** Only the four health dimensions — `classifyCompanyHealth` reads nothing else. */
export type HealthCompanyInput = CompanyHealthStatusInput;

export interface HealthDealInput {
  revenue?: number | string | null;
  cost?: number | string | null;
}

export interface HealthTimeEntryInput {
  hours?: number | string | null;
  user_id?: string | null;
}

export interface HealthUserInput {
  id?: string | null;
  is_billable?: boolean | null;
  /** A plain `int` in both sources, so it stays a number — the `|| 40` default
   * below is arithmetic, not a parse. */
  available_hours?: number | null;
  /** Postgres `numeric`, so it may arrive as a string. */
  hourly_rate?: number | string | null;
}

export function calculateHealthMetrics(
  companies: readonly HealthCompanyInput[],
  deals: readonly HealthDealInput[],
  timeEntries: readonly HealthTimeEntryInput[],
  users: readonly HealthUserInput[],
  // Length of the selected period in days — drives the available-hours capacity
  // for utilization. MUST match the window the time entries were fetched over,
  // or utilization is wrong (a 90-day range against a 30-day capacity reads 3×).
  daysInPeriod: number = 30
): HealthMetrics {
  if (!companies || !deals || !timeEntries || !users) {
    return {
      activeProjectsRed: 0,
      activeProjectsYellow: 0,
      activeProjectsGreen: 0,
      marginPercentage: 0,
      utilizationPercentage: 0,
    };
  }

  // Project health distribution — counts ONLY rated companies, bucketed by worst
  // dimension, via the shared classifier the drilldown also uses (so the bar
  // counts equal the drilldown lists). Unrated companies are excluded entirely.
  let activeProjectsRed = 0;
  let activeProjectsYellow = 0;
  let activeProjectsGreen = 0;

  companies.forEach((company) => {
    const bucket = classifyCompanyHealth(company);
    if (bucket === "red") activeProjectsRed++;
    else if (bucket === "yellow") activeProjectsYellow++;
    else if (bucket === "green") activeProjectsGreen++;
    // bucket === null → not rated → not an "active project" here
  });

  // Calculate margin percentage from deals
  // Use the margin_percentage calculated at the deal level
  // Only include deals with valid revenue AND cost data
  let totalMargin = 0;
  let dealsWithMargin = 0;

  deals.forEach((deal) => {
    // Validate revenue and cost before including in margin calculation
    const { isValid: revenueValid, value: revenue } = validateDealRevenue(deal.revenue);
    const { isValid: costValid, value: cost } = validateDealCost(deal.cost);

    // Only include deals with both valid revenue and cost data
    if (revenueValid && costValid && revenue > 0 && cost !== null && cost !== undefined) {
      const calculatedMargin = ((revenue - cost) / revenue) * 100;
      totalMargin += calculatedMargin;
      dealsWithMargin++;
    }
  });

  const marginPercentage = dealsWithMargin > 0 ? Math.round(totalMargin / dealsWithMargin) : 0;

  // Calculate utilization percentage
  // Utilization = (Hours worked / Available hours) × 100
  // weeksInPeriod scales with the SELECTED period so capacity matches the hours
  // window (a fixed 30 here would 3× the number on a 90-day range).
  const weeksInPeriod = Math.max(daysInPeriod, 1) / 7;

  let totalAvailableHours = 0;
  let totalHoursWorked = 0;

  users.forEach((user) => {
    if (user.is_billable) {
      totalAvailableHours += (user.available_hours || 40) * weeksInPeriod;
    }
  });

  timeEntries.forEach((entry) => {
    totalHoursWorked += parseBigFloat(entry.hours) ?? 0;
  });

  const utilizationPercentage =
    totalAvailableHours > 0
      ? Math.round((totalHoursWorked / totalAvailableHours) * 100)
      : 0;

  // Burdened internal labor cost for the period.
  // Cost = Σ(entry.hours × that user's app_users.hourly_rate) × 1.20 burden.
  // hourly_rate / hours are BigFloat (emitted as strings) — coerce safely.
  const userRateById = new Map<string, number>();
  users.forEach((user) => {
    const rate = parseBigFloat(user.hourly_rate) ?? 0;
    if (user.id && rate > 0) {
      userRateById.set(user.id, rate);
    }
  });

  let laborCostBase = 0; // pre-burden (hours × pay rate)
  let laborCostBurdened = 0; // with 20% WTC burden
  let laborCostHoursMissingRate = 0; // hours we couldn't cost (no rate on file)

  timeEntries.forEach((entry) => {
    const hours = parseBigFloat(entry.hours) ?? 0;
    if (hours <= 0) return;
    const rate = entry.user_id ? userRateById.get(entry.user_id) : undefined;
    if (rate === undefined) {
      laborCostHoursMissingRate += hours;
      return;
    }
    laborCostBase += hours * rate;
    laborCostBurdened += computeBurdenedLaborCost(hours, rate);
  });

  return {
    activeProjectsRed,
    activeProjectsYellow,
    activeProjectsGreen,
    marginPercentage: Math.max(0, marginPercentage), // Ensure non-negative
    utilizationPercentage: Math.min(100, utilizationPercentage), // Cap at 100%
    laborCostBase: Math.round(laborCostBase),
    laborCostBurdened: Math.round(laborCostBurdened),
    laborCostHoursMissingRate: Math.round(laborCostHoursMissingRate * 10) / 10,
    // Confidence indicators
    dealsWithMarginData: dealsWithMargin,
    totalDeals: deals.length,
    marginConfidence: dealsWithMargin / deals.length > 0.8 ? "high" : dealsWithMargin > 0 ? "medium" : "low",
  };
}
